import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, CircularProgress, Avatar,
  Alert, Tooltip, Badge, InputAdornment
} from '@mui/material';
import {
  Add, CalendarMonth, Close, AccessTime, Cancel, Replay,
  CheckCircle, HowToReg, NotificationsActive, Search
} from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

const statusConfig: Record<string, { color: string; bg: string; border: string }> = {
  scheduled:     { color: '#3b82f6', bg: '#eff6ff',  border: '#bfdbfe' },
  confirmed:     { color: '#10b981', bg: '#ecfdf5',  border: '#a7f3d0' },
  'checked-in':  { color: '#8b5cf6', bg: '#f5f3ff',  border: '#ddd6fe' },
  'in-progress': { color: '#f59e0b', bg: '#fffbeb',  border: '#fde68a' },
  completed:     { color: '#059669', bg: '#ecfdf5',  border: '#6ee7b7' },
  cancelled:     { color: '#ef4444', bg: '#fef2f2',  border: '#fecaca' },
  rescheduled:   { color: '#94a3b8', bg: '#f8fafc',  border: '#e2e8f0' },
  'no-show':     { color: '#94a3b8', bg: '#f8fafc',  border: '#e2e8f0' },
};

const Appointments = () => {
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [open, setOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [patients, setPatients] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleForm, setRescheduleForm] = useState({ appointmentDate: '', timeSlot: '' });
  const [form, setForm] = useState({
    patient: '', doctor: '', department: '',
    appointmentDate: dayjs().format('YYYY-MM-DD'),
    timeSlot: '', type: 'consultation', symptoms: '',
    consultationFee: 0, notes: ''
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/appointments', { params: { date, limit: 100 } });
      setAppointments(res.data.data || []);
    } catch { toast.error('Failed to load appointments'); }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (open) {
      Promise.all([
        api.get('/patients', { params: { limit: 200, search: patientSearch } }),
        api.get('/doctors', { params: { limit: 100 } }),
        api.get('/departments')
      ]).then(([p, d, dept]) => {
        setPatients(p.data.data || []);
        setDoctors(d.data.data || []);
        setDepartments(dept.data.data || []);
      });
    }
  }, [open, patientSearch]);

  // Load slots when doctor + date changes
  useEffect(() => {
    if (form.doctor && form.appointmentDate) {
      setLoadingSlots(true);
      setForm(f => ({ ...f, timeSlot: '' }));
      api.get(`/doctors/${form.doctor}/available-slots`, { params: { date: form.appointmentDate } })
        .then(r => setAvailableSlots(r.data.data || []))
        .catch(() => setAvailableSlots([]))
        .finally(() => setLoadingSlots(false));
    }
  }, [form.doctor, form.appointmentDate]);

  // Auto-fill fee when doctor selected
  useEffect(() => {
    if (form.doctor) {
      const doc = doctors.find(d => d._id === form.doctor);
      if (doc) {
        const fee = form.type === 'follow-up' ? doc.followUpFee : form.type === 'emergency' ? doc.emergencyFee : doc.consultationFee;
        setForm(f => ({ ...f, consultationFee: fee || 0, department: doc.department?._id || '' }));
      }
    }
  }, [form.doctor, form.type]);

  const handleSubmit = async () => {
    if (!form.patient || !form.doctor || !form.timeSlot) {
      setError('Patient, doctor and time slot are required');
      return;
    }
    setError(''); setSubmitting(true);
    try {
      await api.post('/appointments', form);
      toast.success('Appointment booked! Notification sent.');
      setOpen(false);
      setForm({ patient: '', doctor: '', department: '', appointmentDate: dayjs().format('YYYY-MM-DD'), timeSlot: '', type: 'consultation', symptoms: '', consultationFee: 0, notes: '' });
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Booking failed');
    } finally { setSubmitting(false); }
  };

  const handleCancel = async () => {
    try {
      await api.post(`/appointments/${selected._id}/cancel`, { reason: cancelReason, cancelledBy: 'admin' });
      toast.success('Appointment cancelled. Patient notified.');
      setCancelOpen(false);
      load();
    } catch { toast.error('Cancellation failed'); }
  };

  const handleReschedule = async () => {
    if (!rescheduleForm.appointmentDate || !rescheduleForm.timeSlot) {
      toast.error('Please select date and time');
      return;
    }
    try {
      await api.post(`/appointments/${selected._id}/reschedule`, rescheduleForm);
      toast.success('Appointment rescheduled. Patient notified.');
      setRescheduleOpen(false);
      load();
    } catch { toast.error('Reschedule failed'); }
  };

  const handleCheckIn = async (id: string) => {
    try {
      await api.post(`/appointments/${id}/check-in`);
      toast.success('Patient checked in');
      load();
    } catch { toast.error('Check-in failed'); }
  };

  const handleComplete = async (id: string) => {
    try {
      await api.post(`/appointments/${id}/complete`);
      toast.success('Appointment completed');
      load();
    } catch { toast.error('Failed'); }
  };

  const sendReminder = async (id: string) => {
    try {
      await api.post(`/appointments/${id}/remind`);
      toast.success('Reminder sent to patient');
    } catch { toast.error('Failed to send reminder'); }
  };

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const dateButtons = Array.from({ length: 7 }, (_, i) => dayjs().add(i - 1, 'day').format('YYYY-MM-DD'));

  const statusCounts = appointments.reduce((acc: any, a) => { acc[a.status] = (acc[a.status] || 0) + 1; return acc; }, {});

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>Appointments</Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.3 }}>{appointments.length} appointments • {dayjs(date).format('DD MMM YYYY')}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setOpen(true); setError(''); }}
          sx={{ borderRadius: 2, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', fontWeight: 600, px: 2.5 }}>
          Book Appointment
        </Button>
      </Box>

      {/* Status Summary */}
      {appointments.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {Object.entries(statusCounts).map(([status, count]) => {
            const sc = statusConfig[status] || statusConfig.scheduled;
            return (
              <Chip key={status} label={`${status}: ${count}`} size="small"
                sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600, fontSize: 11, border: `1px solid ${sc.border}`, textTransform: 'capitalize' }} />
            );
          })}
        </Box>
      )}

      {/* Date Selector */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <AccessTime sx={{ color: '#94a3b8', mr: 0.5 }} />
          {dateButtons.map(d => {
            const isToday = d === dayjs().format('YYYY-MM-DD');
            const isSelected = d === date;
            return (
              <Button key={d} onClick={() => setDate(d)} size="small" variant={isSelected ? 'contained' : 'outlined'}
                sx={{ borderRadius: 2, textTransform: 'none', minWidth: 72, py: 0.8,
                  bgcolor: isSelected ? '#1e40af' : 'transparent', borderColor: isSelected ? '#1e40af' : '#e2e8f0',
                  color: isSelected ? 'white' : isToday ? '#1e40af' : '#64748b',
                  '&:hover': { bgcolor: isSelected ? '#1d4ed8' : '#f8fafc', borderColor: '#1e40af' } }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, display: 'block', lineHeight: 1 }}>{isToday ? 'TODAY' : dayjs(d).format('ddd').toUpperCase()}</Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.2 }}>{dayjs(d).format('D')}</Typography>
                </Box>
              </Button>
            );
          })}
          <TextField type="date" size="small" value={date} onChange={e => setDate(e.target.value)}
            sx={{ ml: 'auto', '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        </Box>
      </Card>

      {/* Appointments Table */}
      <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#3b82f6' }} /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Token', 'Patient', 'Doctor', 'Time', 'Type', 'Status', 'Fee', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#374151', py: 1.5, fontSize: 13, borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {appointments.map(a => {
                  const sc = statusConfig[a.status] || statusConfig.scheduled;
                  const docName = a.doctor?.user?.name || '—';
                  return (
                    <TableRow key={a._id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                      <TableCell>
                        <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: 14 }}>#{a.tokenNumber}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#dbeafe', color: '#1d4ed8', fontSize: 13, fontWeight: 700 }}>
                            {a.patient?.name?.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>{a.patient?.name}</Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{a.patient?.patientId}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Dr. {docName}</Typography>
                        {a.department && <Typography variant="caption" sx={{ color: '#94a3b8' }}>{a.department.name}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af' }}>{a.timeSlot}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={a.type} size="small" variant="outlined" sx={{ textTransform: 'capitalize', fontSize: 11 }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={a.status} size="small"
                          sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600, fontSize: 11, textTransform: 'capitalize', border: `1px solid ${sc.border}` }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{a.consultationFee || 0}</Typography>
                        {a.isPaid && <Chip label="Paid" size="small" sx={{ bgcolor: '#dcfce7', color: '#15803d', fontSize: 10, ml: 0.5 }} />}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {a.status === 'scheduled' || a.status === 'confirmed' ? (
                            <>
                              <Tooltip title="Check In">
                                <IconButton size="small" onClick={() => handleCheckIn(a._id)} sx={{ color: '#8b5cf6', '&:hover': { bgcolor: '#f5f3ff' } }}>
                                  <HowToReg fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Send Reminder">
                                <IconButton size="small" onClick={() => sendReminder(a._id)} sx={{ color: '#f59e0b', '&:hover': { bgcolor: '#fffbeb' } }}>
                                  <NotificationsActive fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reschedule">
                                <IconButton size="small" onClick={() => { setSelected(a); setRescheduleForm({ appointmentDate: '', timeSlot: '' }); setRescheduleOpen(true); }}
                                  sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}>
                                  <Replay fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton size="small" onClick={() => { setSelected(a); setCancelReason(''); setCancelOpen(true); }}
                                  sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}>
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : a.status === 'checked-in' || a.status === 'in-progress' ? (
                            <Tooltip title="Mark Complete">
                              <IconButton size="small" onClick={() => handleComplete(a._id)} sx={{ color: '#10b981', '&:hover': { bgcolor: '#ecfdf5' } }}>
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {appointments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <CalendarMonth sx={{ fontSize: 48, color: '#e2e8f0', display: 'block', mx: 'auto', mb: 1 }} />
                      <Typography sx={{ color: '#94a3b8' }}>No appointments on this date</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Book Appointment Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, color: '#0f172a', borderBottom: '1px solid #f1f5f9' }}>
          Book Appointment
          <IconButton size="small" onClick={() => setOpen(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Search Patient</Typography>
              <TextField fullWidth size="small" placeholder="Type to search..." value={patientSearch}
                onChange={e => setPatientSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
                sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <TextField select fullWidth size="small" label="Select Patient *" value={form.patient} onChange={e => set('patient', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {patients.map(p => <MenuItem key={p._id} value={p._id}>{p.name} — {p.patientId} {p.phone ? `(${p.phone})` : ''}</MenuItem>)}
              </TextField>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Select Doctor *</Typography>
                <TextField select fullWidth size="small" value={form.doctor} onChange={e => set('doctor', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                  {doctors.map(d => <MenuItem key={d._id} value={d._id}>Dr. {d.user?.name} — {d.specialization}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Appointment Type</Typography>
                <TextField select fullWidth size="small" value={form.type} onChange={e => set('type', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                  {['consultation','follow-up','emergency','surgery','diagnostic','online'].map(t => (
                    <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Date *</Typography>
                <TextField fullWidth size="small" type="date" value={form.appointmentDate}
                  onChange={e => set('appointmentDate', e.target.value)}
                  InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>
                  Time Slot * {loadingSlots && <CircularProgress size={10} />}
                </Typography>
                {availableSlots.length > 0 ? (
                  <Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, maxHeight: 120, overflowY: 'auto' }}>
                      {availableSlots.map(slot => (
                        <Chip key={slot.time} label={slot.time} size="small"
                          onClick={() => slot.isAvailable && set('timeSlot', slot.time)}
                          sx={{
                            cursor: slot.isAvailable ? 'pointer' : 'not-allowed',
                            bgcolor: form.timeSlot === slot.time ? '#1e40af' : slot.isAvailable ? '#f1f5f9' : '#fef2f2',
                            color: form.timeSlot === slot.time ? 'white' : slot.isAvailable ? '#374151' : '#94a3b8',
                            fontWeight: form.timeSlot === slot.time ? 700 : 400,
                            fontSize: 11,
                            '&:hover': slot.isAvailable ? { bgcolor: form.timeSlot === slot.time ? '#1d4ed8' : '#e2e8f0' } : {}
                          }} />
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ color: '#94a3b8', mt: 0.5, display: 'block' }}>
                      {availableSlots.filter(s => s.isAvailable).length} slots available
                    </Typography>
                  </Box>
                ) : (
                  <TextField select fullWidth size="small" value={form.timeSlot} onChange={e => set('timeSlot', e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                    {Array.from({ length: 20 }, (_, i) => { const h = Math.floor(i/2)+8; const m = i%2===0?'00':'30'; return `${String(h).padStart(2,'0')}:${m}`; })
                      .map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Consultation Fee (₹)</Typography>
                <TextField fullWidth size="small" type="number" value={form.consultationFee}
                  onChange={e => set('consultationFee', Number(e.target.value))}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Symptoms / Reason</Typography>
                <TextField fullWidth size="small" multiline rows={2} value={form.symptoms}
                  onChange={e => set('symptoms', e.target.value)} placeholder="Describe symptoms or reason"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ borderRadius: 2, fontSize: 12 }}>
              📧 SMS & email confirmation will be sent to the patient automatically.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9' }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' }, px: 3 }}>
            {submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Book & Notify Patient'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          Cancel Appointment
          <IconButton size="small" onClick={() => setCancelOpen(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fef2f2', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{selected.patient?.name}</Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>Dr. {selected.doctor?.user?.name} • {selected.timeSlot}</Typography>
            </Box>
          )}
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>Reason for Cancellation</Typography>
          <TextField fullWidth size="small" multiline rows={3} value={cancelReason}
            onChange={e => setCancelReason(e.target.value)} placeholder="Explain reason..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2, fontSize: 12 }}>Patient will be notified via SMS & email.</Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCancelOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Back</Button>
          <Button variant="contained" color="error" onClick={handleCancel}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Confirm Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleOpen} onClose={() => setRescheduleOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
          Reschedule Appointment
          <IconButton size="small" onClick={() => setRescheduleOpen(false)}><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#eff6ff', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{selected.patient?.name}</Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>Current: Dr. {selected.doctor?.user?.name} • {dayjs(selected.appointmentDate).format('DD MMM')} {selected.timeSlot}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>New Date *</Typography>
              <TextField fullWidth size="small" type="date" value={rescheduleForm.appointmentDate}
                onChange={e => setRescheduleForm(f => ({ ...f, appointmentDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', mb: 0.5, display: 'block' }}>New Time Slot *</Typography>
              <TextField select fullWidth size="small" value={rescheduleForm.timeSlot}
                onChange={e => setRescheduleForm(f => ({ ...f, timeSlot: e.target.value }))}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                {Array.from({ length: 20 }, (_, i) => { const h = Math.floor(i/2)+8; const m = i%2===0?'00':'30'; return `${String(h).padStart(2,'0')}:${m}`; })
                  .map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Box>
          </Box>
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2, fontSize: 12 }}>Patient will be notified of new schedule.</Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRescheduleOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleReschedule}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#1e40af' }}>
            Reschedule & Notify
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Appointments;
